import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Linkedin, Facebook, Instagram } from "lucide-react";

const quickLinks = [
  { label: "The Practice", href: "#about", testid: "footer-link-about" },
  { label: "About Us", href: "/about", testid: "footer-link-about-us" },
  { label: "Services", href: "#services", testid: "footer-link-services" },
  { label: "Contact", href: "#contact", testid: "footer-link-contact" },
  { label: "Blog", href: "/blog", testid: "footer-link-blog" },
];

const socials = [
  { Icon: Linkedin, label: "LinkedIn", testid: "footer-social-linkedin", href: null },
  { Icon: Facebook, label: "Facebook", testid: "footer-social-facebook", href: null },
  { Icon: Instagram, label: "Instagram", testid: "footer-social-instagram", href: "https://instagram.com/ab.co.accountant" },
];

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();

  const goTo = (href) => {
    if (href.startsWith("/")) {
      navigate(href);
      window.scrollTo(0, 0);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/" + href);
    } else {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer data-testid="site-footer" className="relative overflow-hidden bg-navy text-cream">
      <div className="mx-auto max-w-7xl px-6 pt-20 lg:px-12 lg:pt-28">
        <motion.p
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="whitespace-nowrap font-serif text-[14vw] font-medium leading-none tracking-tight text-cream lg:text-[10rem]"
          data-testid="footer-brand"
        >
          AB <span className="italic text-gold">&amp;</span> CO
        </motion.p>

        <div className="mt-16 grid grid-cols-1 gap-12 border-t border-gold/25 py-12 sm:grid-cols-3">
          <div>
            <div className="mb-6 inline-block bg-cream px-4 py-3">
              <img src="/logo.png" alt="AB & Co Accountant" className="h-14 w-auto" />
            </div>
            <p className="font-sans text-xs font-semibold uppercase tracking-[0.24em] text-gold">Office</p>
            <p className="mt-4 font-serif text-lg leading-relaxed text-cream/80">
              8 Ormonde Street<br />Kilkenny, Ireland
            </p>
            <a href="tel:+353871081212" className="mt-3 block font-serif text-lg text-cream/80 transition-colors hover:text-gold">
              087 108 1212
            </a>
            <a href="mailto:info@abaccountant.ie" className="block font-serif text-lg text-cream/80 transition-colors hover:text-gold">
              info@abaccountant.ie
            </a>
            <p className="mt-4 font-serif text-lg text-cream/80" data-testid="footer-hours">
              Mon – Fri · 9:00am – 6:00pm
            </p>
          </div>

          <div>
            <p className="font-sans text-xs font-semibold uppercase tracking-[0.24em] text-gold">Quick Links</p>
            <ul className="mt-4 space-y-3">
              {quickLinks.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    data-testid={l.testid}
                    onClick={(e) => { e.preventDefault(); goTo(l.href); }}
                    className="font-serif text-lg text-cream/80 transition-colors hover:text-gold"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-sans text-xs font-semibold uppercase tracking-[0.24em] text-gold">Follow</p>
            <div className="mt-4 flex gap-4">
              {socials.map(({ Icon, label, testid, href }) => (
                <a
                  key={label}
                  href={href || "#top"}
                  data-testid={testid}
                  aria-label={label}
                  {...(href
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : { onClick: (e) => e.preventDefault() })}
                  className="flex h-11 w-11 items-center justify-center border border-gold/40 text-cream/80 transition-all duration-300 hover:border-gold hover:bg-gold hover:text-navy"
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-cream/50">
              Tax, audit, bookkeeping, payroll and advisory for businesses across Ireland.
            </p>
          </div>
        </div>

        <div className="flex flex-col items-start justify-between gap-3 border-t border-gold/25 py-8 sm:flex-row sm:items-center">
          <p className="text-xs uppercase tracking-[0.2em] text-cream/50" data-testid="footer-copyright">
            © 2026 AB &amp; Co Accountant · Kilkenny, Ireland
          </p>
          <p className="font-serif text-sm italic text-cream/50">Clarity in every number.</p>
        </div>
      </div>
    </footer>
  );
}
